import { useRef } from 'react';
import { useFramework, useScale } from '@/framework';
import { getLayerInfoWithRotate, sampleLayersUniformly } from '@/utils/layer-kit';
import { DirectionType, ResizeOperateType, type ILayerProps, type IPoint } from '@/version-types';
import { SmartGuidesPlugin } from '../../smart-guides';
import { RESIZING_ABSORB_DISTANCE } from '../constants';
import LineList from '../tools/line-list';
import { checkLayerNeedToAlign, getAbsorbOffset, getDirectionVector } from '../tools/utils';

export const useResizeSnap = () => {
    const framework = useFramework();
    const scale = useScale();
    const scaleRef = useRef(scale);
    scaleRef.current = scale;

    // 是否吸附中
    const isAdsorb = useRef<boolean>(false);
    // 吸附状态下的位移
    const adsorbDisplacement = useRef<IPoint>({ x: 0, y: 0 });

    const lineData = useRef<LineList>();

    const proxyBeforeResizing = (operateType: ResizeOperateType, layer?: ILayerProps | null) => {
        if (!layer) return;

        lineData.current = new LineList();

        if (!framework.service.edgelessCanvas.isEdgelessMode) {
            const { width, height } = framework.service.frame.getActiveFrameSize();
            // 舞台边缘
            lineData.current.push({
                width,
                height,
                left: 0,
                top: 0,
                rotate: 0,
                isStage: true,
                id: 'stage_' + Date.now(),
            });
        }

        let layers: ILayerProps[] = [];
        if (layer.parentId) {
            const warpLayer = framework.service.flatLayer.getWrapLayer(layer.id);
            if (warpLayer) {
                layers = framework.service.flatLayer.getChildrenLayers(warpLayer.id);
            }
        } else {
            const originIndex = layer.__originIndex ?? framework.service.frame.getActiveFrameId();
            const activeLayerIds = framework.service.frame.getActiveLayerIds();
            const frame = framework.service.frame.get(originIndex);
            const transform = framework.service.frame.getFrameTransform(originIndex);
            if (frame) {
                // 舞台边缘
                lineData.current.push({
                    width: frame.width,
                    height: frame.height,
                    left: frame.left,
                    top: frame.top - transform.offsetTop,
                    rotate: 0,
                    isStage: true,
                    id: 'stage_' + Date.now(),
                });
            }
            layers = framework.service.frame.getFlatLayers(originIndex, activeLayerIds);
        }

        layers = layers.filter((layer) => {
            return (
                framework.service.edgelessCanvas.getByLayerId(layer.id)?.checkInViewport().intersects &&
                !framework.service.flatLayer.checkIsNeedHidden(layer)
            );
        });

        const directionVector = getDirectionVector(operateType, layer.rotate);
        const currentLayerRect = getLayerInfoWithRotate(layer);
        // 添加参与计算的图层
        sampleLayersUniformly(layers, layer, false).forEach((item) => {
            const beCheckedLayerRect = getLayerInfoWithRotate(item);
            const needToAlign = checkLayerNeedToAlign(currentLayerRect, beCheckedLayerRect, directionVector);
            if (needToAlign) {
                lineData.current!.push(item);
            }
        });
    };

    const proxyResizing = ({
        displacementX,
        displacementY,
        oldLayer,
        operateType,
        direction,
        layer,
    }: {
        // 向左为负, 向右为正
        displacementX: number;
        // 向上为负, 向下为正
        displacementY: number;
        oldLayer: ILayerProps;
        direction: DirectionType;
        operateType: ResizeOperateType;
        /** MouseOperate 在本帧解析的实际操作图层。 */
        layer?: ILayerProps | null;
    }) => {
        if (!layer) return { x: 0, y: 0, isSnap: false };

        const rotate = layer.rotate;
        const isChangeHeight = operateType === ResizeOperateType.TC || operateType === ResizeOperateType.BC;

        /**
         * 求出当前缩放值下, layer 宽高的变化, 用于 block 的计算
         * 因为拖动图层的左边或者上边调整的时候, 会改变图层的 left, top
         * 所以需要用调整后的 left, top 去计算才会准确
         */
        const leftOffset = !isChangeHeight && direction === DirectionType.OPPOSITE ? displacementX : 0;
        const topOffset = isChangeHeight && direction === DirectionType.OPPOSITE ? displacementY : 0;

        // 当前鼠标调整后的图层尺寸(不包含吸附)
        const resizedLayer = {
            ...oldLayer,
            left: oldLayer.left + leftOffset,
            top: oldLayer.top + topOffset,
            width: Math.floor(oldLayer.width + displacementX * direction),
            height: Math.floor(oldLayer.height + displacementY * direction),
        };

        const oldLayerRect = getLayerInfoWithRotate(oldLayer);
        // 需要基于 resize 前的中心点进行旋转
        const centerPoint = {
            x: oldLayer.width / 2 + oldLayer.left,
            y: oldLayer.height / 2 + oldLayer.top,
        };
        const newLayerRect = getLayerInfoWithRotate(resizedLayer, centerPoint);

        const directionVector = getDirectionVector(operateType, rotate);

        const lineNearest = lineData.current?.getNearestLineForResize(
            {
                ...newLayerRect,
                rotate: 0,
            },
            directionVector,
        );

        if (lineNearest) {
            const adsorbDistance = Math.max(1, Math.ceil(RESIZING_ABSORB_DISTANCE / scaleRef.current));
            // x 轴吸附位移, 向左为负, 向右为正
            const nearestOffsetX = -lineNearest.vertical.distance;
            // y 轴吸附位移, 向上为负, 向下为正
            const nearestOffsetY = -lineNearest.horizontal.distance;

            // 进入吸附范围
            if (Math.abs(nearestOffsetX) < adsorbDistance || Math.abs(nearestOffsetY) < adsorbDistance) {
                if (!isAdsorb.current) {
                    let offset1 = Infinity;
                    let offset2 = Infinity;
                    // 上下吸附
                    if (Math.abs(nearestOffsetX) < adsorbDistance) {
                        const directionX = directionVector.x === 0 ? 0 : directionVector.x < 0 ? -1 : 1;
                        // 吸附的偏移量
                        const additionalRectSize =
                            Math.abs(newLayerRect.width + nearestOffsetX * directionX) - oldLayerRect.width;
                        offset1 = getAbsorbOffset({
                            rotate,
                            isChangeHeight,
                            isHorizontal: false,
                            additionalRectSize,
                        });
                    }
                    // 左右吸附
                    if (Math.abs(nearestOffsetY) < adsorbDistance) {
                        const directionY = directionVector.y === 0 ? 0 : directionVector.y < 0 ? -1 : 1;
                        // 吸附的偏移量
                        const additionalRectSize =
                            Math.abs(newLayerRect.height + nearestOffsetY * directionY) - oldLayerRect.height;
                        offset2 = getAbsorbOffset({
                            rotate,
                            isChangeHeight,
                            isHorizontal: true,
                            additionalRectSize,
                        });
                    }

                    // @TODO 偏移距离大于吸附距离时不吸附
                    const minOffset = Math.abs(offset1) < Math.abs(offset2) ? offset1 : offset2;

                    if (isChangeHeight) {
                        adsorbDisplacement.current.y = minOffset * direction;
                    } else {
                        adsorbDisplacement.current.x = minOffset * direction;
                    }
                    isAdsorb.current = true;

                    // @TODO 吸附距离大于吸附距离时, 不吸附
                    // const absorbedOffset = minOffset - mouseOffset;
                    // const isOverAdsorbDistance = Math.abs(absorbedOffset) > Math.abs(adsorbDistance);
                    // if(isOverAdsorbDistance) {
                    //     isAdsorb.current = false;
                    // }
                    // else {
                    //     if(isChangeHeight) {
                    //         adsorbDisplacement.current.y = isOverAdsorbDistance ? displacementY : minOffset;
                    //     }
                    //     else {
                    //         adsorbDisplacement.current.x = isOverAdsorbDistance ? displacementX : minOffset;
                    //     }
                    //     isAdsorb.current = true;
                    // }
                }
            } else {
                adsorbDisplacement.current.x = displacementX;
                adsorbDisplacement.current.y = displacementY;
                isAdsorb.current = false;
            }
        } else {
            adsorbDisplacement.current.x = displacementX;
            adsorbDisplacement.current.y = displacementY;
        }

        return {
            ...adsorbDisplacement.current,
            isSnap: isAdsorb.current,
        };
    };

    const resetResizeSnap = () => {
        lineData.current = undefined;
        isAdsorb.current = false;
    };

    const proxyAfterResizing = (layer: ILayerProps | null | undefined, isEnd: boolean) => {
        if (layer) {
            // 对齐 UI 展示
            const lines = lineData.current?.getLines(layer, true);
            if (lines) {
                SmartGuidesPlugin.get(framework)?.setLines(lines);
            }
        }

        // 清除临时数据
        if (isEnd) {
            resetResizeSnap();
        }
    };

    return {
        proxyResizing,
        proxyBeforeResizing,
        proxyAfterResizing,
        resetResizeSnap,
    };
};
